import { Injectable } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { TypedFormData } from '@src/app/model/typed-form-data';
import { PropertyField } from '@src/app/model/property-field';
import { Documentation } from '@src/app/model/documentation';
import { GroupedFormData } from '@src/app/model/grouped-form-data';
import { cloneDeep } from 'lodash';

@Injectable({
  providedIn: 'root',
})
export class FormService {
  /**
   * Creates a list of form groups for a generic object type
   *
   * @param documentation - Documentation with some metadata on how to make the forms and a comment to display to users
   * @param subjects - The object to be edited in these forms
   */
  createFormArray(documentation: Documentation, subjects: JSON[]): GroupedFormData[] {
    return subjects.map(subject => this.createForm(documentation, subject));
  }

  /**
   * Creates a form group for a generic object type
   *
   * @param documentation - Documentation with some metadata on how to make the form and a comment to display to users
   * @param subjectJsonInput - The object to be edited in this form
   */
  createForm(documentation: Documentation, subjectJsonInput: JSON): GroupedFormData {
    const formControls: any = {};
    const typedFormDataArray = documentation.fields.map(
      field => this._createTypedFormData(field, subjectJsonInput, formControls)
    );

    return new GroupedFormData(new FormGroup(formControls), typedFormDataArray, subjectJsonInput);
  }

  /**
   * Validates a list of form groups
   *
   * @param groupedFormDataArray - The array of form grouped form data containing the form groups to validate
   * @returns Whether any of the form groups are invalid or not
   */
  validateFormArray(groupedFormDataArray: GroupedFormData[]): { valid: boolean, errors: string[] } {
    const validationResults = groupedFormDataArray.map(
      groupedFormData => this.validateForm(groupedFormData.formGroup)
    );
    return {
      valid: validationResults.every(v => v.valid),
      errors: validationResults.reduce<string[]>((errors, currentResult) => {
        return errors.concat(currentResult.errors);
      }, []),
    };
  }

  /**
   * Validates a form group
   *
   * @param formGroup - The form group to validate
   * @returns Whether the form group is valid or not
   */
  validateForm(formGroup: FormGroup): { valid: boolean, errors: string[] } {
    const errorMessages = [];
    Object.keys(formGroup.controls).forEach((key) => {
      formGroup.controls[key].updateValueAndValidity();
      if (!formGroup.controls[key].valid) {
        Object.keys(formGroup.controls[key].errors).forEach((errorKey) => {
          errorMessages.push(`Field: ${key}, Invalid reason: ${errorKey}`);
        });
      }
    });
    return { valid: formGroup.valid, errors: errorMessages };
  }

  /**
   * Updates the subject array of subjects with the data from the form and creates a new version of the subjects with it.
   *
   * Does not alter the original subject objects.
   *
   * @param groupedFormDataArray - The array of GroupedFormData containing the form groups to get the data from and the original subjects to update
   * @returns The updated subjects
   */
  getFormArrayData(groupedFormDataArray: GroupedFormData[]): JSON[] {
    return groupedFormDataArray.map(fg => this.getFormData(fg));
  }

  /**
   * Updates the subject with the data from the form and creates a new version of the subject with it.
   *
   * Does not alter the original subject object.
   *
   * @param formData - The GroupedFormData containing the form group to get the data from and the original subject to update
   * @returns The updated subject
   */
  getFormData(formData: GroupedFormData): JSON {
    const updatedSubject: JSON = cloneDeep(formData.subject);
    Object.keys(formData.formGroup.controls).forEach((key) => {
      if (formData.formGroup.controls[key].value !== undefined) {
        updatedSubject[key] = formData.formGroup.controls[key].value;
      }
    });
    return updatedSubject as unknown as JSON; // JSON where?
  }

  private _createTypedFormData(
    field: PropertyField,
    subject: JSON,
    group: object
  ): TypedFormData<any[]> | TypedFormData<boolean> | TypedFormData<number> | TypedFormData<object>
    | TypedFormData<string> {
    switch (field.fieldType) {
      case 'array': {
        group[field.fieldName] = this._createFormControl<any[]>(field, subject[field.fieldName], []);
        return new TypedFormData<any[]>(field, subject[field.fieldName]);
      }
      case 'boolean': {
        group[field.fieldName] = this._createFormControl<boolean>(field, subject[field.fieldName], false);
        return new TypedFormData<boolean>(field, subject[field.fieldName]);
      }
      case 'number': {
        group[field.fieldName] = this._createFormControl<number>(field, subject[field.fieldName], 0);
        return new TypedFormData<number>(field, subject[field.fieldName]);
      }
      case 'object': {
        group[field.fieldName] = this._createFormControl(field, subject[field.fieldName], {});
        return new TypedFormData(field, subject[field.fieldName]);
      }
      case 'string': {
        group[field.fieldName] = this._createFormControl<string>(field, subject[field.fieldName], null as string);
        return new TypedFormData<string>(field, subject[field.fieldName]);
      }
      default: {
        group[field.fieldName] = this._createFormControl<string>(field, subject[field.fieldName], null as string);
        return new TypedFormData<string>(field, subject[field.fieldName]);
      }
    }
  }

  private _createFormControl<T>(propertyField: PropertyField, fieldValue: T, defaultValue: T): FormControl {
    if (propertyField.required) {
      return new FormControl(fieldValue ?? defaultValue, Validators.required);
    } else {
      return new FormControl(fieldValue);
    }
  }
}
